import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import { API_BASE_URL, fetchJson } from './api';

const STATUS_OPTIONS = ['provided', 'unknown', 'not_applicable', 'not_received', 'not_commissioned', 'unreadable', 'withheld'];

function fieldPayload(field, draft, metadata) {
    const raw = draft[field.name];
    const explicitStatus = draft[`${field.name}:status`];
    const status = explicitStatus || (raw === '' || raw == null ? 'unknown' : 'provided');
    return {
        field_key: field.key, entity_id: metadata.entityId, value_type: field.type, value_status: status,
        raw_value: status !== 'provided' ? null : field.type === 'boolean' ? raw === 'true' || raw === true : raw,
        unit: field.type === 'area' ? draft.unit : field.unit,
        basis: field.type === 'area' ? draft.area_basis : null,
        currency: field.type === 'money' ? metadata.currency : null,
        vat_basis: field.type === 'money' ? metadata.vatBasis : null,
        date_precision: field.type === 'date' ? draft[`${field.name}:precision`] || 'exact' : null,
        effective_at: metadata.effectiveDate || null,
        metadata: { applicability_reason: metadata.reason, jurisdiction: draft.jurisdiction || null, assessment_role: draft.assessment_role || null }
    };
}

export { fieldPayload };

export default function ReportEvidenceEditor({ reference, mode = 'surveyor', code = '', reportInstance = 'project', location, authHeaders, onSaved }) {
    const [groups, setGroups] = useState([]);
    const [evidence, setEvidence] = useState({ assertions: [], decisions: [], conflicts: [] });
    const [groupKey, setGroupKey] = useState('');
    const [entityId, setEntityId] = useState('site');
    const [draft, setDraft] = useState({});
    const [currency, setCurrency] = useState('');
    const [vatBasis, setVatBasis] = useState('unknown');
    const [effectiveDate, setEffectiveDate] = useState('');
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [reload, setReload] = useState(0);
    const [saved, setSaved] = useState(false);
    const [enabled, setEnabled] = useState(null);
    const [canExplainGaps, setCanExplainGaps] = useState(false);
    const [explanationSaved, setExplanationSaved] = useState('');
    const [expandedExplanation, setExpandedExplanation] = useState('');
    const query = new URLSearchParams({ mode, report_instance: reportInstance, ...(code ? { code } : {}) });
    const base = `${API_BASE_URL}/project/${encodeURIComponent(reference)}/report-evidence`;
    const suffix = query.toString();

    useEffect(() => {
        setGroupKey(''); setEntityId('site'); setDraft({}); setCurrency(''); setVatBasis('unknown');
        setEffectiveDate(''); setReason(''); setSaved(false);
        setExplanationSaved('');
        setExpandedExplanation('');
    }, [reference, reportInstance, location]);

    useEffect(() => {
        let cancelled = false;
        setError('');
        Promise.all([
            fetchJson(`${base}/catalog?${suffix}`, { headers: authHeaders?.() }),
            fetchJson(`${base}?${suffix}`, { headers: authHeaders?.() })
        ]).then(([catalog, current]) => {
            if (cancelled) return;
            setEnabled(catalog.enabled !== false);
            setCanExplainGaps(catalog.permissions?.can_explain_gaps === true);
            setGroups(catalog.groups || []);
            setEvidence(current);
        }).catch((requestError) => { if (!cancelled) setError(requestError.message || 'Evidence records could not be loaded.'); });
        return () => { cancelled = true; };
    }, [base, suffix, authHeaders, reload]);

    const visibleGroups = groups.filter((group) => (!location || group.portal === location)
        && (reportInstance === 'project' ? group.scope === 'project' : group.scope === 'period')
        && (group.report !== 'interim' || reportInstance.startsWith('interim:'))
        && (group.report !== 'initial' || !reportInstance.startsWith('interim:')));
    const activeGroup = visibleGroups.find((group) => group.key === groupKey);
    const records = activeGroup ? [...new Set(evidence.assertions.filter((assertion) => activeGroup.fields.some((field) => field.key === (assertion.metadata?.collection_field_key || assertion.field_key)))
        .map((assertion) => assertion.entity_id))] : [];

    function selectRecord(nextGroupKey, nextEntityId) {
        const nextGroup = groups.find((group) => group.key === nextGroupKey);
        const nextDraft = {};
        setCurrency(''); setVatBasis('unknown'); setEffectiveDate(''); setReason('');
        for (const assertion of evidence.assertions || []) {
            if (assertion.source_channel !== 'portal' || assertion.entity_id !== nextEntityId) continue;
            const field = nextGroup?.fields.find((candidate) => candidate.key === (assertion.metadata?.collection_field_key || assertion.field_key));
            if (!field) continue;
            nextDraft[field.name] = assertion.raw_value;
            nextDraft[`${field.name}:status`] = assertion.value_status;
            nextDraft[`${field.name}:precision`] = assertion.date_precision || 'exact';
            if (assertion.currency) setCurrency(assertion.currency);
            if (assertion.vat_basis) setVatBasis(assertion.vat_basis);
            if (assertion.effective_at) setEffectiveDate(assertion.effective_at.slice(0, 10));
            if (assertion.metadata?.applicability_reason) setReason(assertion.metadata.applicability_reason);
        }
        for (const explanation of evidence.gap_explanation_history || evidence.gap_explanations || []) {
            if (explanation.entity_id !== nextEntityId) continue;
            const field = nextGroup?.fields.find((candidate) => candidate.key === explanation.field_key);
            if (field) nextDraft[`${field.name}:gap_version_id`] = explanation.id;
        }
        for (const explanation of evidence.gap_explanations || []) {
            if (explanation.entity_id !== nextEntityId || explanation.status !== 'active') continue;
            const field = nextGroup?.fields.find((candidate) => candidate.key === explanation.field_key);
            if (!field) continue;
            nextDraft[`${field.name}:gap_explanation`] = explanation.explanation;
            nextDraft[`${field.name}:gap_action`] = explanation.required_action || '';
            nextDraft[`${field.name}:expected_date`] = explanation.expected_date || '';
            nextDraft[`${field.name}:gap_id`] = explanation.id;
        }
        setGroupKey(nextGroupKey);
        setEntityId(nextEntityId);
        setDraft(nextDraft);
        setSaved(false);
        setExplanationSaved('');
        setExpandedExplanation('');
    }

    async function saveExplanation(field, status = 'active') {
        setBusy(true); setError(''); setExplanationSaved('');
        try {
            const result = await fetchJson(`${base}/gap-explanations?${suffix}`, {
                method: 'POST', headers: authHeaders?.({ 'Content-Type': 'application/json' }) || { 'Content-Type': 'application/json' },
                body: JSON.stringify({ report_instance: reportInstance, field_key: field.key, entity_id: entityId,
                    expected_id: draft[`${field.name}:gap_version_id`] || draft[`${field.name}:gap_id`] || null, status,
                    explanation: draft[`${field.name}:gap_explanation`] || '', required_action: draft[`${field.name}:gap_action`] || null,
                    expected_date: draft[`${field.name}:expected_date`] || null })
            });
            setDraft((previous) => ({ ...previous, [`${field.name}:gap_id`]: status === 'withdrawn' ? null : result.explanation?.id || previous[`${field.name}:gap_id`] || null,
                [`${field.name}:gap_version_id`]: result.explanation?.id || previous[`${field.name}:gap_version_id`] || previous[`${field.name}:gap_id`] || null,
                ...(status === 'withdrawn' ? { [`${field.name}:gap_explanation`]: '', [`${field.name}:gap_action`]: '', [`${field.name}:expected_date`]: '' } : {}) }));
            setExplanationSaved(status === 'withdrawn' ? `${field.label} (withdrawn)` : field.label);
            setReload((value) => value + 1);
        } catch (requestError) { setError(requestError.message || 'Missing-evidence explanation could not be saved.'); }
        finally { setBusy(false); }
    }

    async function save() {
        if (!activeGroup) return;
        setBusy(true);
        setError('');
        setSaved(false);
        try {
            const assertions = activeGroup.fields.map((field) => fieldPayload(field, draft,
                { entityId, currency, vatBasis, effectiveDate, reason }));
            await fetchJson(`${base}/assertions?${suffix}`, {
                method: 'POST', headers: authHeaders?.({ 'Content-Type': 'application/json' }) || { 'Content-Type': 'application/json' },
                body: JSON.stringify({ report_instance: reportInstance, assertions })
            });
            setSaved(true);
            setReload((value) => value + 1);
            onSaved?.();
        } catch (requestError) {
            setError(requestError.message || 'Evidence record could not be saved.');
        } finally { setBusy(false); }
    }

    const reviewedFacts = evidence.reviewed_facts || [];
    if (enabled === false) return null;
    return (
        <Box component="section" aria-label="Source evidence records" sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
                <Typography component="h2" sx={{ fontSize: 16, fontWeight: 750 }}>Evidence records</Typography>
                <Chip size="small" label={reportInstance === 'project' ? 'Project scope' : reportInstance} />
                {evidence.status ? <Chip size="small" color={evidence.status === 'ready' ? 'success' : 'warning'}
                    label={evidence.status === 'ready' ? 'Verified view ready' : 'Factual review required'} /> : null}
            </Stack>
            {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
            {saved ? <Alert severity="success" sx={{ mb: 1.5 }}>Saved - factual review pending</Alert> : null}
            {explanationSaved ? <Alert severity="success" sx={{ mb: 1.5 }}>Explanation saved for {explanationSaved}. Qualified draft context retained.</Alert> : null}
            <TextField select fullWidth size="small" label="Record type" value={groupKey} onChange={(event) => selectRecord(event.target.value, 'site')}>
                <MenuItem value="">Select record</MenuItem>
                {visibleGroups.map((group) => <MenuItem key={group.id} value={group.key}>{group.label}</MenuItem>)}
            </TextField>
            {activeGroup ? (
                <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ my: 1.5 }}>
                        <TextField select fullWidth size="small" label="Record" value={entityId} onChange={(event) => selectRecord(groupKey, event.target.value)}>
                            {[...new Set(['site', entityId, ...records])].map((identifier) => <MenuItem key={identifier} value={identifier}>{identifier}</MenuItem>)}
                        </TextField>
                        {activeGroup.repeatable && mode !== 'funder' ? <Button startIcon={<AddIcon />} onClick={() => selectRecord(groupKey, window.crypto?.randomUUID?.() || `record-${Date.now()}`)} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>Add record</Button> : null}
                        <TextField fullWidth size="small" type="date" label="Effective date" InputLabelProps={{ shrink: true }} value={effectiveDate} onChange={(event) => setEffectiveDate(event.target.value)} />
                    </Stack>
                    {activeGroup.fields.some((field) => field.type === 'money') ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
                            <TextField select fullWidth size="small" label="Currency" value={currency} onChange={(event) => setCurrency(event.target.value)}>
                                <MenuItem value="">Unknown</MenuItem>{['GBP', 'EUR', 'USD'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                            </TextField>
                            <TextField select fullWidth size="small" label="VAT basis" value={vatBasis} onChange={(event) => setVatBasis(event.target.value)}>
                                {['unknown', 'exclusive', 'inclusive', 'mixed'].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                            </TextField>
                        </Stack>
                    ) : null}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                        {activeGroup.fields.map((field) => {
                            const options = field.type === 'boolean' ? ['true', 'false'] : field.options;
                            const status = draft[`${field.name}:status`] || (draft[field.name] == null || draft[field.name] === '' ? 'unknown' : 'provided');
                            return (
                                <Box key={field.key} sx={{ minWidth: 0 }}>
                                    <TextField fullWidth size="small" label={field.label} value={draft[field.name] ?? ''} select={Boolean(options)}
                                        disabled={mode === 'funder' || ['not_applicable', 'not_received', 'not_commissioned', 'unreadable', 'withheld'].includes(status)}
                                        type={field.type === 'date' ? draft[`${field.name}:precision`] === 'month' ? 'month' : 'date' : ['integer', 'decimal', 'money', 'area'].includes(field.type) ? 'number' : 'text'}
                                        InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                                        inputProps={['money', 'decimal', 'area'].includes(field.type) ? { step: 'any' } : field.type === 'integer' ? { step: 1 } : undefined}
                                        onChange={(event) => setDraft((previous) => ({ ...previous, [field.name]: event.target.value, [`${field.name}:status`]: event.target.value === '' ? 'unknown' : 'provided' }))}>
                                        {options ? [<MenuItem key="unknown" value="">Unknown</MenuItem>, ...options.map((option) => <MenuItem key={option} value={option}>{option === 'true' ? 'Yes' : option === 'false' ? 'No' : option}</MenuItem>)] : null}
                                    </TextField>
                                    <Stack direction="row" spacing={1} sx={{ mt: 0.6 }}>
                                        <TextField select fullWidth size="small" label={`${field.label} status`} value={status} disabled={mode === 'funder'}
                                            onChange={(event) => setDraft((previous) => ({ ...previous, [`${field.name}:status`]: event.target.value }))}>
                                            {STATUS_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option.replace(/_/g, ' ')}</MenuItem>)}
                                        </TextField>
                                        {field.type === 'date' ? <TextField select size="small" label="Precision" value={draft[`${field.name}:precision`] || 'exact'}
                                            onChange={(event) => setDraft((previous) => ({ ...previous, [`${field.name}:precision`]: event.target.value }))}>
                                            {['exact', 'month', 'approximate'].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                                        </TextField> : null}
                                        {canExplainGaps && status !== 'provided' && status !== 'not_applicable' ? <Tooltip title={`Explain missing evidence for ${field.label}`}>
                                            <IconButton size="small" aria-label={`Explain missing evidence for ${field.label}`} sx={{ flexShrink: 0, width: 40, height: 40 }}
                                                onClick={() => setExpandedExplanation((previous) => previous === field.name ? '' : field.name)}><QuestionAnswerOutlinedIcon fontSize="small" /></IconButton>
                                        </Tooltip> : null}
                                    </Stack>
                                    {canExplainGaps && (expandedExplanation === field.name || draft[`${field.name}:gap_id`]) ? (
                                        <Stack spacing={0.75} sx={{ mt: 1 }}>
                                            <TextField fullWidth size="small" multiline minRows={2} label={`Why ${field.label.toLowerCase()} evidence is unavailable`}
                                                value={draft[`${field.name}:gap_explanation`] || ''}
                                                onChange={(event) => setDraft((previous) => ({ ...previous, [`${field.name}:gap_explanation`]: event.target.value }))} />
                                            <TextField fullWidth size="small" label={`${field.label} required action`} value={draft[`${field.name}:gap_action`] || ''}
                                                onChange={(event) => setDraft((previous) => ({ ...previous, [`${field.name}:gap_action`]: event.target.value }))} />
                                            <TextField fullWidth size="small" type="date" label={`${field.label} expected date`} InputLabelProps={{ shrink: true }}
                                                value={draft[`${field.name}:expected_date`] || ''}
                                                onChange={(event) => setDraft((previous) => ({ ...previous, [`${field.name}:expected_date`]: event.target.value }))} />
                                            <Button size="small" startIcon={<SaveOutlinedIcon />} sx={{ alignSelf: 'start' }}
                                                disabled={busy || !String(draft[`${field.name}:gap_explanation`] || '').trim()} onClick={() => saveExplanation(field)}>Save explanation</Button>
                                            {draft[`${field.name}:gap_id`] ? <Button size="small" startIcon={<ClearOutlinedIcon />} sx={{ alignSelf: 'start' }}
                                                disabled={busy} onClick={() => saveExplanation(field, 'withdrawn')}>Withdraw explanation</Button> : null}
                                        </Stack>
                                    ) : null}
                                </Box>
                            );
                        })}
                    </Box>
                    <TextField fullWidth size="small" multiline minRows={2} label="Applicability reason / review context" value={reason} onChange={(event) => setReason(event.target.value)} sx={{ mt: 1.5 }} />
                    {mode !== 'funder' ? <Button variant="contained" startIcon={<SaveOutlinedIcon />} disabled={busy} onClick={save} sx={{ my: 1.5 }}>Save record</Button> : null}
                    <Box sx={{ mt: 1, overflowWrap: 'anywhere' }}>
                        {reviewedFacts.filter((assertion) => assertion.entity_id === entityId
                            && activeGroup.fields.some((field) => field.key === (assertion.metadata?.collection_field_key || assertion.field_key))).map((assertion) => (
                            <Typography key={assertion.id} variant="body2">{assertion.field_key}: {String(assertion.normalized_value)} {assertion.unit || ''} ({assertion.source_channel}, reviewed)</Typography>
                        ))}
                    </Box>
                </>
            ) : null}
        </Box>
    );
}